/** @jsxImportSource react */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { fetchPlatformStatus, type PlatformStatus } from '@/services/platformStatus.service';
import { isImpersonationActive } from '@/utils/authToken';
import { getRoleFromToken } from '@/utils/tokenValidation';
import { useThemeLanguage } from '@/contexts/ThemeLanguageContext';

const DEFAULT_MAINTENANCE_MESSAGE_ES =
  'La plataforma está en mantenimiento. Vuelve a intentar más tarde.';
const DEFAULT_PAUSE_MESSAGE_ES =
  'Temporalmente no se aceptan nuevas vacantes ni aplicaciones. Inténtalo más tarde.';

export interface PlatformStatusContextValue {
  status: PlatformStatus | null;
  loading: boolean;
  refreshing: boolean;
  refresh: () => Promise<PlatformStatus | null>;
  isMaintenanceActive: boolean;
  isHiringPaused: boolean;
  shouldShowHiringPauseUI: () => boolean;
  hiringPauseMessage: string;
  maintenanceMessage: string;
}

const PlatformStatusContext = createContext<PlatformStatusContextValue | null>(null);

export function PlatformStatusProvider({ children }: { children: React.ReactNode }) {
  const { t } = useThemeLanguage();
  const [status, setStatus] = useState<PlatformStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const refresh = useCallback(async (): Promise<PlatformStatus | null> => {
    setRefreshing(true);
    try {
      const next = await fetchPlatformStatus();
      setStatus(next);
      return next;
    } catch {
      setStatus(null);
      return null;
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const interval = window.setInterval(() => void refresh(), 60_000);
    return () => window.clearInterval(interval);
  }, [refresh]);

  const isMaintenanceActive = Boolean(status?.maintenance_mode);
  const isHiringPaused = Boolean(status?.vacancies_globally_paused) && !isMaintenanceActive;

  const maintenanceMessage = useMemo(() => {
    const custom = status?.maintenance_message?.trim();
    if (custom) return custom;
    return t('maintenance.defaultMessage') || DEFAULT_MAINTENANCE_MESSAGE_ES;
  }, [status?.maintenance_message, t]);

  const hiringPauseMessage = useMemo(() => {
    const custom = status?.vacancies_pause_message?.trim();
    if (custom) return custom;
    return t('hiringPause.defaultMessage') || DEFAULT_PAUSE_MESSAGE_ES;
  }, [status?.vacancies_pause_message, t]);

  const shouldShowHiringPauseUI = useCallback(() => {
    if (!isHiringPaused) return false;
    if (isImpersonationActive()) return false;
    const token =
      typeof localStorage !== 'undefined' ? localStorage.getItem('user_token') : null;
    const role = token ? getRoleFromToken(token) : null;
    if (role === 'admin') return false;
    return true;
  }, [isHiringPaused]);

  const value = useMemo<PlatformStatusContextValue>(
    () => ({
      status,
      loading,
      refreshing,
      refresh,
      isMaintenanceActive,
      isHiringPaused,
      shouldShowHiringPauseUI,
      hiringPauseMessage,
      maintenanceMessage,
    }),
    [
      status,
      loading,
      refreshing,
      refresh,
      isMaintenanceActive,
      isHiringPaused,
      shouldShowHiringPauseUI,
      hiringPauseMessage,
      maintenanceMessage,
    ],
  );

  return React.createElement(PlatformStatusContext.Provider, { value }, children);
}

export function usePlatformStatus(): PlatformStatusContextValue {
  const ctx = useContext(PlatformStatusContext);
  if (!ctx) {
    throw new Error('usePlatformStatus must be used within PlatformStatusProvider');
  }
  return ctx;
}
